/**
 * WorkOSGate — auth gate for workos mode.
 *
 * Shows a splash while initAuth() restores the session (via the server-side
 * refresh cookie), a "Sign in with WorkOS" screen when unauthenticated, and the
 * app once signed in. The OAuth code exchange is handled entirely server-side
 * (GET /v1/auth/workos/callback), so there is no client-side /callback route.
 */

import { type ReactNode } from 'react'
import { useAuth } from '../../auth/AuthProvider'

export function WorkOSGate({ children }: { children: ReactNode }) {
  const { isLoading, isAuthenticated, signIn } = useAuth()

  if (isLoading) {
    return <AuthSplash message="Loading…" />
  }

  if (!isAuthenticated) {
    return <SignInScreen onSignIn={() => signIn()} />
  }

  return <>{children}</>
}

function AuthSplash({ message }: { message: string }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1220] text-[#4a6280] text-[13px]">
      {message}
    </div>
  )
}

function SignInScreen({ onSignIn }: { onSignIn: () => void }) {
  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0b1220] px-4">
      <div className="w-full max-w-[460px] bg-[rgba(16,26,43,0.9)] border border-[rgba(148,163,184,0.12)] rounded-2xl p-8 shadow-2xl text-center">
        <div className="flex items-center justify-center gap-2.5 mb-6">
          <img src="/assets/brand/logo.svg" alt="" className="h-7 w-7" aria-hidden="true" />
          <h1 className="text-lg font-bold text-[#e2ecf9] m-0 tracking-[-0.01em]">Sign in</h1>
        </div>
        <p className="text-[13px] text-[#4a6280] leading-[1.6] mb-6">
          Sign in with your organization account to continue to Mission Control.
        </p>
        <button
          type="button"
          onClick={onSignIn}
          className="w-full h-[40px] rounded-lg bg-[#06b6d4] text-[#04222b] text-sm font-bold cursor-pointer hover:bg-[#22cfe8] transition-colors"
        >
          Sign in with WorkOS
        </button>
      </div>
    </div>
  )
}
